import { Controller } from "@hotwired/stimulus";

const backgroundColors = [
  "rgba(16, 185, 129, 0.3)",
  "rgba(64, 131, 207, 0.3)",
  "rgba(255, 99, 132, 0.3)",
  "rgba(255, 206, 86, 0.3)",
  "rgba(54, 162, 235, 0.3)",
  "rgba(75, 192, 192, 0.3)",
  "rgba(153, 102, 255, 0.3)",
  "rgba(255, 159, 64, 0.3)",
];

const borderColors = [
  "rgba(16, 185, 129, 1)",
  "rgba(64, 131, 207, 1)",
  "rgba(255, 99, 132, 1)",
  "rgba(255, 206, 86, 1)",
  "rgba(54, 162, 235, 1)",
  "rgba(75, 192, 192, 1)",
  "rgba(153, 102, 255, 1)",
  "rgba(255, 159, 64, 1)",
];

export default class extends Controller {
  async connect() {
    this.connected = true;

    const [{ Chart }, { default: ChartDataLabels }] = await Promise.all([
      import("../application/lib/charts"),
      import("chartjs-plugin-datalabels"),
    ]);

    if (!this.connected || this.chart) return;

    this.chart = new Chart(this.element.getContext("2d"), {
      plugins: [ChartDataLabels],
      ...this.chartParams,
    });
  }

  disconnect() {
    this.connected = false;
    this.chart?.destroy();
    this.chart = null;
  }

  get chartParams() {
    const data = {
      labels: JSON.parse(this.element.dataset.labels),
      datasets: this.element.dataset.datasets
        ? JSON.parse(this.element.dataset.datasets).map((dataset, index) => ({
            ...dataset,
            borderColor: borderColors[index],
            backgroundColor: backgroundColors[index],
            borderWidth: 1,
          }))
        : [
            {
              data: JSON.parse(this.element.dataset.values),
              backgroundColor: "rgba(16, 185, 129, 0.3)",
              borderColor: "rgb(16, 185, 129)",
              borderWidth: 1,
            },
          ],
    };

    return {
      type: "bar",
      data,
      options: {
        layout: {
          padding: {},
        },
        scales: {
          x: {
            stacked: this.element.dataset.stacked === "true",
            grid: {
              display: false,
            },
          },
          y: {
            stacked: this.element.dataset.stacked === "true",
            ticks: {
              callback: (value) => {
                return new Intl.NumberFormat("en-GB", {
                  style: this.element.dataset.format || "decimal",
                  currency: this.element.dataset.currency || "GBP",
                  minimumFractionDigits: 0,
                }).format(value);
              },
              color: (context) => {
                if (
                  this.element.dataset.showAllTicks !== "true" &&
                  parseInt(context.tick.value) !== context.tick.value
                ) {
                  return "transparent";
                }
              },
            },
          },
        },
        plugins: {
          legend: {
            display: this.element.dataset.legend === "true",
          },
          tooltip: {
            callbacks: {
              label: (context) => {
                let label = context.dataset.label || "";

                if (label) {
                  label += ": ";
                }

                if (context.parsed.y !== null) {
                  label += new Intl.NumberFormat("en-GB", {
                    style: this.element.dataset.format || "decimal",
                    currency: this.element.dataset.currency || "GBP",
                    minimumFractionDigits: 0,
                  }).format(context.parsed.y);
                }

                return label;
              },
            },
          },
          datalabels: {
            display: false,
            labels: {
              value: {
                backgroundColor: (context) => {
                  return context.dataset.borderColor;
                },
                formatter: (value) => {
                  return new Intl.NumberFormat("en-GB", {
                    style: this.element.dataset.format || "decimal",
                    currency: this.element.dataset.currency || "GBP",
                    minimumFractionDigits: 0,
                  }).format(value);
                },
                color: "white",
                align: "end",
                anchor: "end",
                borderRadius: 4,
                font: {
                  weight: "bold",
                },
              },
            },
          },
        },
      },
    };
  }
}
